import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accessControlAction } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { PendingInvitation } from '@/types';

export function useInvitations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['invitations', user?.id],
    queryFn: async (): Promise<PendingInvitation[]> => {
      if (!user) return [];

      const result = await accessControlAction<{ invitations: PendingInvitation[] }>(
        'list-my-invitations'
      );

      if (result.error) throw new Error(result.error);
      return result.data?.invitations ?? [];
    },
    enabled: !!user,
    refetchOnWindowFocus: true,
    // Bottennavigeringens inbjudningsbadge ska upptäcka nya inbjudningar
    // medan appen är öppen (ingen polling i bakgrunden — TanStack Querys
    // default refetchIntervalInBackground: false).
    refetchInterval: 60_000,
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (pendingId: string) => {
      const result = await accessControlAction<{ assignment: any; error?: string }>(
        'accept-invitation',
        { pending_id: pendingId }
      );

      if (result.error) throw new Error(result.error);
      if (result.data && 'error' in result.data && result.data.error) {
        throw new Error(result.data.error as string);
      }
      return result.data;
    },
    onMutate: async (pendingId) => {
      // Cancel any outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ['invitations', user?.id] });

      // Snapshot previous value
      const previous = queryClient.getQueryData<PendingInvitation[]>(['invitations', user?.id]);

      // Optimistically remove the accepted invitation
      queryClient.setQueryData<PendingInvitation[]>(
        ['invitations', user?.id],
        (old) => old?.filter((inv) => inv.id !== pendingId) ?? []
      );

      return { previous };
    },
    onError: (_err, _pendingId, context) => {
      // Roll back on error
      if (context?.previous) {
        queryClient.setQueryData(['invitations', user?.id], context.previous);
      }
    },
    onSettled: () => {
      // Refetch invitations, devices, and keys to pick up auto-generated key
      queryClient.invalidateQueries({ queryKey: ['invitations', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['devices', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['keys'] });
      queryClient.invalidateQueries({ queryKey: ['all-keys', user?.id] });
    },
  });
}

export function useDeclineInvitation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (pendingId: string) => {
      const result = await accessControlAction<{ success: boolean }>(
        'decline-invitation',
        { pending_id: pendingId }
      );
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onMutate: async (pendingId) => {
      await queryClient.cancelQueries({ queryKey: ['invitations', user?.id] });
      const previous = queryClient.getQueryData<PendingInvitation[]>(['invitations', user?.id]);

      queryClient.setQueryData<PendingInvitation[]>(
        ['invitations', user?.id],
        (old) => old?.filter((inv) => inv.id !== pendingId) ?? []
      );

      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['invitations', user?.id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', user?.id] });
    },
  });
}
